import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { FolderOpen, Lock, Globe, type LucideIcon } from "lucide-react";
import { COLLECTION_DETAIL } from "@/routes";
import { UserAvatar } from "@/components/shared/UserAvatar";
import type { Collection } from "@/types/collections";
import { collectionDisplayTitle } from "@/hooks/useCollections";
import { CAPTION, CARD_TITLE } from "@/lib/typography";

const MAX_VISIBLE_AVATARS = 3;

interface CollectionCardProps {
  collection: Collection;
  className?: string;
}

/** A collection's cover: one image, two side by side, or one large beside two stacked. */
export function CoverMosaic({ thumbnails }: { thumbnails: string[] }) {
  const count = thumbnails.length;

  if (count === 1) {
    return (
      <div className="aspect-[4/3] w-full overflow-hidden rounded-xl">
        <img src={thumbnails[0]} alt="" loading="lazy" className="h-full w-full object-cover" />
      </div>
    );
  }

  if (count === 2) {
    return (
      <div className="aspect-[4/3] w-full overflow-hidden rounded-xl grid grid-cols-2 gap-0.5">
        {thumbnails.map((url, i) => (
          <img key={i} src={url} alt="" loading="lazy" className="h-full w-full object-cover" />
        ))}
      </div>
    );
  }

  // 3 thumbnails: one large left, two stacked right
  return (
    <div className="aspect-[4/3] w-full overflow-hidden rounded-xl grid grid-cols-2 grid-rows-2 gap-0.5">
      <img
        src={thumbnails[0]}
        loading="lazy"
        alt=""
        className="row-span-2 h-full w-full object-cover"
      />
      <img src={thumbnails[1]} alt="" loading="lazy" className="h-full w-full object-cover" />
      <img src={thumbnails[2]} alt="" loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
}

export function EmptyCover({ icon: Icon = FolderOpen }: { icon?: LucideIcon }) {
  return (
    <div className="flex aspect-[4/3] w-full items-center justify-center rounded-xl bg-muted">
      <Icon className="h-8 w-8 text-muted-foreground" />
    </div>
  );
}

export function CollectionCard({ collection, className }: CollectionCardProps) {
  const navigate = useNavigate();
  const members = collection.members ?? [];
  const thumbnails = collection.cover_thumbnails ?? [];
  const visibleMembers = members.slice(0, MAX_VISIBLE_AVATARS);
  const overflow = members.length > MAX_VISIBLE_AVATARS
    ? collection.collaborator_count - MAX_VISIBLE_AVATARS
    : 0;

  return (
    <button
      onClick={() => navigate(COLLECTION_DETAIL({ collectionId: collection.collection_id }))}
      className={cn(
        "flex w-full flex-col items-start gap-2.5 text-left transition-opacity hover:opacity-90",
        className,
      )}
    >
      {/* Cover */}
      {thumbnails.length > 0 ? (
        <CoverMosaic thumbnails={thumbnails} />
      ) : (
        <EmptyCover />
      )}

      {/* Info */}
      <div className="flex w-full flex-col gap-1 min-w-0">
        <span className={cn(CARD_TITLE, "truncate")}>{collectionDisplayTitle(collection.title)}</span>

        <div className="flex items-center justify-between">
          <span className={CAPTION}>
            {collection.item_count} {collection.item_count === 1 ? "item" : "items"}
          </span>

          <div className="flex items-center gap-1.5">
            {collection.visibility === "private" ? (
              <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            {/* Member avatars */}
            {visibleMembers.length > 0 && (
              <div className="flex -space-x-1.5">
                {visibleMembers.map((m) => (
                  <UserAvatar
                    key={m.user_id}
                    avatarUrl={m.avatar_url}
                    name={m.name}
                    username={m.username}
                    size="sm"
                    className="border border-background"
                  />
                ))}
                {overflow > 0 && (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full border border-background bg-muted text-ui-badge font-medium text-muted-foreground">
                    +{overflow}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
